<script setup lang="ts">
import { ref, markRaw, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTicketStore } from '@/stores/useTicketStore'
import type { Ticket } from '@/stores/useTicketStore'
import TicketList from '@/components/features/tickets/TicketList.vue'
import TicketCreateModal from '@/components/features/tickets/TicketCreateModal.vue'
import { ArrowPathIcon, PlusIcon, ChevronLeftIcon, InboxIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import TicketDetail from '@/components/features/tickets/TicketDetail.vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import SelectWithAvatar from '@/components/common/selects/SelectWithAvatar.vue'

const route = useRoute()
const router = useRouter()
const ticketStore = useTicketStore()
const isCreateModalOpen = ref(false)
const selectedTickets = ref<Ticket[]>([])

const searchQuery = ref('')
const selectedStatuses = ref<string[]>(['NEW', 'OPEN', 'WAITING', 'PAUSED', 'RESOLVED'])

const statusOptions = [
  { id: 'NEW', text: 'NOUVEAU', color: 'blue' },
  { id: 'OPEN', text: 'OUVERT', color: 'green' },
  { id: 'WAITING', text: 'EN ATTENTE', color: 'orange' },
  { id: 'PAUSED', text: 'EN PAUSE', color: 'gray' },
  { id: 'CLOSED', text: 'FERMÉ', color: 'red' },
  { id: 'RESOLVED', text: 'RÉSOLU', color: 'teal' },
]

// Removed filteredTickets computed property

const arrowPathIcon = markRaw(ArrowPathIcon)
const plusIcon = markRaw(PlusIcon)
const chevronLeftIcon = markRaw(ChevronLeftIcon)
const xMarkIcon = markRaw(XMarkIcon)

watch(
  () => route.params.ticketId,
  async (ticketId) => {
    if (ticketId && typeof ticketId === 'string') {
      await ticketStore.fetchTicketById(ticketId)
    } else {
      ticketStore.$patch({ ticket: null })
      await handleRefresh()
    }
  },
  { immediate: true },
)

async function handleRefresh() {
  await ticketStore.fetchAllTickets({
    search: searchQuery.value,
    statuses: selectedStatuses.value,
  })
}

// Watch for filter changes to trigger re-fetch
let refreshTimeout: ReturnType<typeof setTimeout> | undefined = undefined
function debouncedRefresh() {
  if (refreshTimeout !== undefined) clearTimeout(refreshTimeout)
  refreshTimeout = setTimeout(() => {
    handleRefresh()
  }, 500)
}

watch(searchQuery, () => {
  debouncedRefresh()
})

watch(
  selectedStatuses,
  () => {
    debouncedRefresh()
  },
  { deep: true },
)

function openCreateModal() {
  isCreateModalOpen.value = true
}

function closeCreateModal() {
  isCreateModalOpen.value = false
}

async function handleCreateTicket(ticket: { title: string; description: string; files: File[] }) {
  try {
    await ticketStore.createTicket(ticket)

    closeCreateModal()

    await handleRefresh()
  } catch (error) {
    console.error('Error creating ticket:', error)
  }
}

function handleSelectionChanged(tickets: Ticket[]) {
  selectedTickets.value = tickets
}

async function goBackToList() {
  ticketStore.$patch({ ticket: null })
  router.push('/tickets')
  await handleRefresh()
}
</script>

<template>
  <div class="w-full flex flex-col gap-4 p-8">
    <div class="flex flex-col gap-12">
      <!-- List header -->
      <template v-if="!ticketStore.ticket">
        <div class="flex items-center gap-4 justify-between w-full">
          <div class="flex items-center gap-2">
            <Bs2Title title="Tickets" />
            <span v-if="!ticketStore.loading" class="text-sm text-gray-500 font-medium mt-1"> {{ ticketStore.tickets.length }} ticket(s) </span>
          </div>
          <div class="flex gap-3 flex-wrap">
            <ButtonDaisy text="Recharger" :variant="'secondary'" :icon="arrowPathIcon" @click="handleRefresh" :disabled="ticketStore.loading" />
            <ButtonDaisy text="Créer Ticket" :variant="'primary'" :icon="plusIcon" @click="openCreateModal" />
          </div>
        </div>
      </template>

      <!-- Detail header -->
      <template v-else>
        <div class="flex items-center gap-3">
          <ButtonDaisy text="Retour" :variant="'primary'" :icon="chevronLeftIcon" @click="goBackToList" />
        </div>
      </template>
    </div>

    <div class="card bg-white rounded-lg p-4">
      <div v-if="!ticketStore.ticket" class="flex flex-wrap gap-4 mb-6">
        <div class="w-full max-w-xs">
          <label class="form-control w-full">
            <div class="label py-1">
              <span class="label-text font-semibold text-gray-700">Rechercher</span>
            </div>
            <div class="relative">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Titre, référence..."
                class="input input-bordered input-sm w-full bg-gray-50 focus:bg-white transition-colors"
              />
              <div v-if="searchQuery" class="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button @click="searchQuery = ''" class="hover:text-error transition-colors">
                  <component :is="xMarkIcon" class="h-4 w-4 text-gray-400" />
                </button>
              </div>
            </div>
          </label>
        </div>

        <div class="w-full max-w-md">
          <SelectWithAvatar
            v-model="selectedStatuses"
            label="Statuts"
            :options="statusOptions"
            multiple
            size="sm"
            :searchable="false"
            :multiselectRemove="false"
          />
        </div>
      </div>

      <TicketDetail v-if="ticketStore.ticket" :ticket="ticketStore.ticket" />

      <div
        v-else-if="route.params.ticketId && ticketStore.loading"
        class="flex flex-col items-center justify-center py-16 px-8 gap-4 text-center text-gray-500"
      >
        <ArrowPathIcon class="w-12 h-12 animate-spin" />
        <p class="text-lg m-0">Chargement du ticket...</p>
      </div>

      <div
        v-else-if="ticketStore.tickets.length === 0"
        class="flex flex-col items-center justify-center py-16 px-8 gap-4 text-center text-gray-500 card bg-white rounded-lg p-4"
      >
        <InboxIcon class="w-12 h-12" />
        <p class="text-lg m-0">Aucun ticket</p>
      </div>

      <TicketList v-else :tickets="ticketStore.tickets" :loading="ticketStore.loading" @selectionChanged="handleSelectionChanged" />
    </div>

    <TicketCreateModal :isOpen="isCreateModalOpen" @close="closeCreateModal" @create="handleCreateTicket" />
  </div>
</template>

<style scoped></style>
